import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { getPrintContentBySheetId } from '../services/ExportService';
import PrintViewControls from './PrintViewControls';
import PrintViewNotation from './PrintViewNotation';
import { useAuth } from '../contexts/AuthContext';
import { getSheetById } from '../services/SheetStorageService';
import logger from '../services/LoggingService';

/**
 * PrintView component for displaying a print-friendly version of a sheet
 * This component is used when directly accessing the print URL
 */
const PrintView = () => {
  const { sheetId } = useParams();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [printContent, setPrintContent] = useState('');
  const [sheetData, setSheetData] = useState(null);
  
  // Fetch user's lineup connections for this sheet
  const fetchLineupConnections = async () => {
    if (!isAuthenticated || !sheetId) return null;
    
    try {
      const response = await fetch(`/api/sheets/${sheetId}/lineups`, {
        credentials: 'include'
      });

      if (!response.ok) {
        logger.error('PrintView', 'Failed to fetch lineup connections, status:', response.status);
        return null;
      }

      const result = await response.json();
      const userConnections = result.data.userConnections || [];
      
      // Return all connections to display them all
      return userConnections;
    } catch (error) {
      logger.error('PrintView', 'Error fetching lineup connections:', error);
      return null;
    }
  };

  useEffect(() => {
    const fetchPrintContent = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch lineup data first
        const lineupData = await fetchLineupConnections();
        
        // Get display parameters - must match defaults in PrintViewControls.js
        const includeChordProgressions = searchParams.get('chords') !== 'false';  // Default to true
        const includeSectionColors = searchParams.get('color') !== 'false';  // Default to true
        const minifyContent = searchParams.get('minify') === 'true';  // Default to false
        const pageBreakBeforeChords = searchParams.get('pagebreak') !== 'false';  // Default to true
        const includeLyrics = searchParams.get('lyrics') !== 'false';  // Default to true
        const includeLineup = searchParams.get('lineup') !== 'false';  // Default to true
        const includeNotation = searchParams.get('notation') !== 'false';  // Default to true
        
        // Get column visibility parameters - must match defaults in PrintViewControls.js
        const showSection = searchParams.get('section') !== 'false';  // Default to true
        const showPart = searchParams.get('part') !== 'false';  // Default to true
        const showBars = searchParams.get('bars') !== 'false';  // Default to true
        const showNotes = searchParams.get('notes') !== 'false';  // Default to true
        const showLyricsColumn = searchParams.get('lyricsColumn') !== 'false';  // Default to true
        const showHeaders = searchParams.get('header') === 'true';  // Default to false
        const rotateSection = searchParams.get('rotateSection') === 'true';  // Default to false
        const showEnergy = searchParams.get('energy') !== 'false';  // Default to true
        const showChordsColumn = searchParams.get('chordsColumn') !== 'false';  // Default to true

        const options = {
          includeChordProgressions,
          includeSectionColors,
          minifyContent,
          pageBreakBeforeChords,
          includeLyrics,
          includeLineup,
          lineup: lineupData, // Add lineup data for print generation
          includeNotation: false, // We handle notation separately in React
          showSection,
          showPart,
          showBars,
          showNotes,
          showLyricsColumn,
          showHeaders,
          rotateSection,
          showEnergy,
          showChordsColumn
        };

        logger.debug('PrintView', 'Using perform view options:', options);

        // Get the sheet data for notations
        const sheet = await getSheetById(sheetId);
        setSheetData(sheet);
        
        const result = await getPrintContentBySheetId(sheetId, options);
        setPrintContent(result.printContent);
      } catch (err) {
        logger.error('PrintView', 'Error loading print content:', err);
        setError(err.message || 'Failed to load sheet for printing');
      } finally {
        setLoading(false);
      }
    };
    
    if (sheetId) {
      fetchPrintContent();
    } else {
      setError('No sheet ID provided');
      setLoading(false);
    }
  }, [sheetId, searchParams, isAuthenticated]);
  
  // If still loading, show a loading indicator
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mb-4"></div>
          <p>Preparing print view...</p>
        </div>
      </div>
    );
  }
  
  // If there was an error, show an error message
  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded max-w-md">
          <h2 className="font-bold mb-2">Error</h2>
          <p>{error}</p>
          <button 
            onClick={() => window.history.back()} 
            className="mt-4 bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }
  
  // Check if notation should be displayed
  const showNotation = searchParams.get('notation') !== 'false';
  
  // Split the HTML content to insert notation at the placeholder
  let contentBeforePlaceholder = printContent;
  let contentAfterPlaceholder = '';
  
  const placeholderMarker = '<div id="notation-placeholder"></div>';
  const placeholderIndex = printContent.indexOf(placeholderMarker);
  
  if (placeholderIndex !== -1) {
    contentBeforePlaceholder = printContent.substring(0, placeholderIndex);
    contentAfterPlaceholder = printContent.substring(placeholderIndex + placeholderMarker.length);
  }
  
  // Render the print content with notation inserted at the placeholder position
  return (
    <>
      <PrintViewControls />
      <div className="print-view pb-20">
        {/* Render content before placeholder */}
        <div dangerouslySetInnerHTML={{ __html: contentBeforePlaceholder }} />
        
        {/* Render notation at placeholder position */}
        {showNotation && sheetData?.notations && sheetData.notations.length > 0 && (
          <PrintViewNotation notations={sheetData.notations} />
        )}
        
        {/* Render content after placeholder (lineup and rest) */}
        <div dangerouslySetInnerHTML={{ __html: contentAfterPlaceholder }} />
      </div>
    </>
  );
};

export default PrintView;
